import React, { useEffect, useMemo, useRef, useState } from "react";
import { loadStripe } from "@stripe/stripe-js";
import {
  Elements,
  PaymentElement,
  useElements,
  useStripe,
} from "@stripe/react-stripe-js";
import { Alert, Divider, Spin } from "antd";
import { useSelector } from "react-redux";
import { ModalComponent, ButtonComponent, CancelButton } from "@comps/components";
import { makeRequestStateless } from "@utils/helpers";
import { createPaymentIntent, getPaymentStatus } from "./requests";

/**
 * Card payment for a booking deposit, using Stripe's Payment Element.
 *
 * Replaces the old iframe → Blade page → Stripe.js v2 → Charges flow. Three
 * things worth knowing before changing this:
 *
 *  - Card fields are rendered by Stripe inside their own iframe. Card details
 *    never touch this app's DOM, which is what keeps us out of heavier PCI
 *    scope. Do not add your own card number inputs.
 *  - 3D Secure / SCA is handled by confirmPayment. UK cards require it; the old
 *    Charges API could not do it, which is why payments were failing.
 *  - A successful confirmPayment does NOT mean the booking is marked paid. The
 *    webhook does that. We poll the status endpoint afterwards.
 *
 * Props:
 *   bookingId  - booking to pay for
 *   onPaid()   - called once the backend confirms the booking is paid
 *   onCancel() - called when the user dismisses without paying
 */

// loadStripe must not run on every render, and the key is only known at runtime,
// so cache one promise per publishable key.
const stripePromiseCache = {};
const getStripePromise = (key) => {
  if (!stripePromiseCache[key]) {
    stripePromiseCache[key] = loadStripe(key);
  }
  return stripePromiseCache[key];
};

const money = (value) => `£${Number(value || 0).toFixed(2)}`;

const StripePaymentModal = (props) => {
  const { bookingId } = props;
  const crmStyle = useSelector((state) => state.common.crmStyle) || {};

  const [intent, setIntent] = useState(null);
  const [loadError, setLoadError] = useState(null);

  useEffect(() => {
    if (!bookingId) {
      setLoadError("No booking was supplied for payment.");
      return;
    }

    makeRequestStateless(
      createPaymentIntent,
      bookingId,
      (data) => setIntent(data),
      (err, res) => setLoadError(res?.msg || "Could not start the payment. Please try again.")
    );
  }, [bookingId]);

  const stripePromise = useMemo(
    () => (intent?.publishable_key ? getStripePromise(intent.publishable_key) : null),
    [intent?.publishable_key]
  );

  const options = useMemo(() => {
    if (!intent?.client_secret) return null;
    return {
      clientSecret: intent.client_secret,
      appearance: {
        theme: "stripe",
        variables: {
          colorPrimary: crmStyle.bg_color || "#0a7d59",
          borderRadius: "6px",
          fontFamily: "inherit",
        },
      },
    };
  }, [intent?.client_secret, crmStyle.bg_color]);

  const body = () => {
    if (loadError) {
      return <Alert type="error" showIcon message="Payment unavailable" description={ loadError } />;
    }

    if (!intent || !options || !stripePromise) {
      return (
        <div className="da-text-center da-py-32">
          <Spin />
          <p className="da-mt-16">Preparing secure payment…</p>
        </div>
      );
    }

    return (
      <Elements stripe={ stripePromise } options={ options }>
        <CheckoutForm
          intent={ intent }
          bookingId={ bookingId }
          crmStyle={ crmStyle }
          onPaid={ props.onPaid }
          onCancel={ props.onCancel }
        />
      </Elements>
    );
  };

  return (
    <ModalComponent
      width={ 560 }
      mainTitle="Payment"
      subTitle={ intent?.invoice_no ? `Invoice ${intent.invoice_no}` : "Booking Deposit" }
      visible={ true }
      footer={ [] }
      onCancel={ props.onCancel }
    >
      { body() }
    </ModalComponent>
  );
};

const CheckoutForm = ({ intent, bookingId, crmStyle, onPaid, onCancel }) => {
  const stripe = useStripe();
  const elements = useElements();

  const [submitting, setSubmitting] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [error, setError] = useState(null);
  const cancelled = useRef(false);

  useEffect(() => () => { cancelled.current = true; }, []);

  const breakdown = intent.breakdown || {};

  /**
   * The webhook marks the booking paid, and it can land a moment after the card
   * is confirmed. Poll rather than assuming.
   */
  const waitForConfirmation = (attempt = 0) => {
    if (cancelled.current) return;

    makeRequestStateless(
      getPaymentStatus,
      bookingId,
      (data) => {
        if (cancelled.current) return;

        if (data?.paid) {
          setConfirming(false);
          onPaid(data);
          return;
        }

        if (attempt >= 8) {
          // Card succeeded but our record hasn't caught up. Never tell the user
          // the payment failed here — the money has been taken.
          setConfirming(false);
          onPaid({ ...data, pending_confirmation: true });
          return;
        }

        setTimeout(() => waitForConfirmation(attempt + 1), 1500);
      },
      () => {
        if (cancelled.current) return;
        if (attempt >= 8) {
          setConfirming(false);
          onPaid({ pending_confirmation: true });
          return;
        }
        setTimeout(() => waitForConfirmation(attempt + 1), 1500);
      }
    );
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (!stripe || !elements || submitting) return;

    setSubmitting(true);
    setError(null);

    // redirect: "if_required" keeps the customer inside this modal for normal
    // card payments, while still allowing Stripe to redirect for payment methods
    // (and 3DS flows) that genuinely need it.
    const result = await stripe.confirmPayment({
      elements,
      redirect: "if_required",
      confirmParams: {
        return_url: `${window.location.origin}/payment-success`,
      },
    });

    if (cancelled.current) return;

    if (result.error) {
      // card_error / validation_error are the user's problem to fix; anything
      // else is ours, and the backend has logged it.
      setError(result.error.message || "Payment failed. Please try another card.");
      setSubmitting(false);
      return;
    }

    const status = result.paymentIntent?.status;

    if (status === "succeeded" || status === "processing") {
      setSubmitting(false);
      setConfirming(true);
      waitForConfirmation();
      return;
    }

    setError("Payment could not be completed. Please try again.");
    setSubmitting(false);
  };

  if (confirming) {
    return (
      <div className="da-text-center da-py-32">
        <Spin />
        <p className="da-mt-16">Payment received — confirming your booking…</p>
      </div>
    );
  }

  return (
    <form onSubmit={ handleSubmit }>
      <div className="da-mb-16">
        <div className="da-d-flex da-justify-between">
          <span>Deposit ({ breakdown.deposit_percent }% of { money(breakdown.total_price) })</span>
          <strong>{ money(breakdown.deposit) }</strong>
        </div>
        <div className="da-d-flex da-justify-between">
          <span>Booking fee</span>
          <strong>{ money(breakdown.booking_fee) }</strong>
        </div>
        <Divider className="da-my-8" />
        <div className="da-d-flex da-justify-between">
          <span><strong>Total to pay now</strong></span>
          <strong>{ money(breakdown.total) }</strong>
        </div>
      </div>

      <PaymentElement options={ { layout: "tabs" } } />

      { error && (
        <Alert className="da-mt-16" type="error" showIcon message={ error } />
      ) }

      <div className="da-mt-24 da-d-flex da-justify-end">
        <CancelButton onClick={ onCancel } />
        <ButtonComponent
          type="primary"
          htmlType="submit"
          state={ submitting }
          disabled={ !stripe || submitting }
          style={ { background: crmStyle.bg_color, borderColor: crmStyle.border_color, marginLeft: 8 } }
        >
          { submitting ? "Processing…" : `Pay ${money(breakdown.total)}` }
        </ButtonComponent>
      </div>
    </form>
  );
};

export default StripePaymentModal;
